/**
 * Correlation analysis schemas.
 */
import { z } from 'zod';
import { createDataResponseSchema, isValidMetric, metricUnits } from "./common.js";
// ============================================================================
// Common schemas
// ============================================================================
/** HRV statistics schema */
export const hrvStatsSchema = z
    .object({
    mean_hr: z.number().nullable().meta({ description: 'Mean heart rate during period' }),
    mean_hrv: z.number().nullable().meta({ description: 'Mean HRV (RMSSD) during period' }),
    mean_stress: z.number().nullable().meta({ description: 'Mean stress level during period' }),
    sample_count: z.number().int().meta({ description: 'Number of data samples' }),
    sample_minutes: z.number().meta({ description: 'Total minutes of data' }),
    stddev_hr: z.number().nullable().meta({ description: 'Standard deviation of HR' }),
    stddev_hrv: z.number().nullable().meta({ description: 'Standard deviation of HRV' }),
    stddev_stress: z.number().nullable().meta({ description: 'Standard deviation of stress level' }),
})
    .meta({ id: 'HrvStats' });
/** HRV stats with baseline delta */
export const hrvStatsWithDeltaSchema = hrvStatsSchema
    .extend({
    hr_delta_from_baseline: z.number().nullable().meta({ description: 'HR change from baseline' }),
    hrv_delta_from_baseline: z.number().nullable().meta({ description: 'HRV change from baseline' }),
    stress_delta_from_baseline: z
        .number()
        .nullable()
        .meta({ description: 'Stress level change from baseline' }),
})
    .meta({ id: 'HrvStatsWithDelta' });
// ============================================================================
// Baseline endpoint
// ============================================================================
/** Baseline query parameters */
export const baselineQuerySchema = z
    .object({
    reference_date: z.string().optional().meta({
        description: 'Reference date for baseline calculation (defaults to today)',
        example: '2024-01-15',
    }),
})
    .meta({ id: 'BaselineQuery' });
/** Baseline result data */
export const baselineDataSchema = z
    .object({
    hrv: z.object({
        avg7day: z.number().nullable(),
        avg30day: z.number().nullable(),
        trend_percent: z.number().nullable().meta({ description: 'Change from previous 30-day period' }),
    }),
    period: z.object({
        end: z.string(),
        start: z.string(),
    }),
    resting_hr: z.object({
        avg7day: z.number().nullable(),
        avg30day: z.number().nullable(),
        trend_percent: z.number().nullable(),
    }),
    stress: z.object({
        avg7day: z.number().nullable(),
        avg30day: z.number().nullable(),
        trend_percent: z.number().nullable().meta({ description: 'Change from previous 30-day period' }),
    }),
})
    .meta({ id: 'BaselineData' });
/** Baseline response */
export const baselineResponseSchema = createDataResponseSchema(baselineDataSchema).meta({
    id: 'BaselineResponse',
});
// ============================================================================
// HRV-Activities endpoint
// ============================================================================
/**
 * The autonomic metric the HRV-context analysis correlates against. HRV is the
 * default, but not everyone has continuous HRV — heart rate and stress are
 * denser and let the analysis still produce a meaningful productivity
 * correlation.
 */
export const hrvContextMetricSchema = z.enum(['hrv_rmssd', 'heart_rate', 'stress_level']).meta({
    description: 'Autonomic metric to correlate against (default: hrv_rmssd)',
    id: 'HrvContextMetric',
});
/** HRV-Activities query parameters */
export const hrvActivitiesQuerySchema = z
    .object({
    context_metric: hrvContextMetricSchema.optional().meta({ description: 'Metric to correlate against' }),
    period_days: z
        .string()
        .optional()
        .meta({ description: 'Number of days to analyze (default 30)', example: '30' }),
})
    .meta({ id: 'HrvActivitiesQuery' });
/** Productivity correlation */
export const productivityCorrelationSchema = hrvStatsWithDeltaSchema
    .extend({
    category: z.string().meta({ description: 'RescueTime category name' }),
    correlation_coefficient: z.number().nullable().meta({ description: 'Pearson correlation (-1 to 1)' }),
})
    .meta({ id: 'ProductivityCorrelation' });
/** Location correlation */
export const locationCorrelationSchema = hrvStatsWithDeltaSchema
    .extend({
    location_name: z.string().meta({ description: 'Location name' }),
    visit_count: z.number().int().meta({ description: 'Number of visits' }),
})
    .meta({ id: 'LocationCorrelation' });
/** Activity correlation */
export const activityCorrelationSchema = hrvStatsWithDeltaSchema
    .extend({
    activity_type: z.string().meta({ description: 'Activity type' }),
    avg_duration_min: z
        .number()
        .optional()
        .meta({ description: 'Average duration in minutes (undefined for point activities)' }),
    occurrences: z.number().int().meta({ description: 'Number of occurrences' }),
})
    .meta({ id: 'ActivityCorrelation' });
/** HRV-Activities result data */
export const hrvActivitiesDataSchema = z
    .object({
    baseline: hrvStatsSchema,
    context_metric: hrvContextMetricSchema.meta({
        description: 'The metric the correlation_coefficient was computed against',
    }),
    correlations: z.object({
        activities: z.array(activityCorrelationSchema),
        locations: z.array(locationCorrelationSchema),
        productivity: z.array(productivityCorrelationSchema),
    }),
    period: z.object({
        days: z.number().int(),
        end: z.string(),
        start: z.string(),
    }),
})
    .meta({ id: 'HrvActivitiesData' });
/** HRV-Activities response */
export const hrvActivitiesResponseSchema = createDataResponseSchema(hrvActivitiesDataSchema).meta({
    id: 'HrvActivitiesResponse',
});
// ============================================================================
// Activity Impact endpoint
// ============================================================================
/** Activity type enum for impact analysis */
export const activityImpactTypeSchema = z
    .enum(['productivity_category', 'productivity_app', 'location', 'tag', 'activity_type'])
    .meta({
    description: 'Type of activity to analyze',
    example: 'productivity_category',
    id: 'ActivityImpactType',
});
/** Activity Impact query parameters */
export const activityImpactQuerySchema = z
    .object({
    activity_type: activityImpactTypeSchema,
    period_days: z.string().optional().meta({ description: 'Days to analyze (default 90)', example: '90' }),
    window_minutes: z
        .string()
        .optional()
        .meta({ description: 'Minutes before/after (default 30)', example: '30' }),
})
    .meta({ id: 'ActivityImpactQuery' });
/** Time window stats */
export const timeWindowStatsSchema = z
    .object({
    mean: z.number().nullable(),
    sample_count: z.number().int(),
    stddev: z.number().nullable(),
})
    .meta({ id: 'TimeWindowStats' });
/** Activity Impact result data */
export const activityImpactDataSchema = z
    .object({
    activity: z.string().meta({ description: 'Activity name/pattern searched' }),
    activity_type: activityImpactTypeSchema,
    avg_duration_min: z.number().meta({ description: 'Average duration in minutes' }),
    hr_timeline: z.object({
        after15min: timeWindowStatsSchema,
        after30min: timeWindowStatsSchema,
        before15min: timeWindowStatsSchema,
        before30min: timeWindowStatsSchema,
        during: timeWindowStatsSchema,
    }),
    hrv_timeline: z.object({
        after15min: timeWindowStatsSchema,
        after30min: timeWindowStatsSchema,
        before15min: timeWindowStatsSchema,
        before30min: timeWindowStatsSchema,
        during: timeWindowStatsSchema,
    }),
    occurrences: z.number().int().meta({ description: 'Number of activity occurrences found' }),
    stress_timeline: z.object({
        after15min: timeWindowStatsSchema,
        after30min: timeWindowStatsSchema,
        before15min: timeWindowStatsSchema,
        before30min: timeWindowStatsSchema,
        during: timeWindowStatsSchema,
    }),
})
    .meta({ id: 'ActivityImpactData' });
/** Activity Impact response */
export const activityImpactResponseSchema = createDataResponseSchema(activityImpactDataSchema).meta({
    id: 'ActivityImpactResponse',
});
// ============================================================================
// Event Probability endpoint
// ============================================================================
/** Event trigger type */
export const eventTriggerTypeSchema = z.enum(['activity', 'tag']).meta({
    description: 'Type of trigger event',
    example: 'activity',
    id: 'EventTriggerType',
});
/** Event Probability request body */
export const eventProbabilityBodySchema = z
    .object({
    lag_windows: z
        .array(z.string())
        .optional()
        .meta({ description: 'Time windows to analyze', example: ['12h', '24h', '36h', '48h'] }),
    outcome_pattern: z
        .string()
        .meta({ description: 'Regex pattern for outcome tag', example: 'painkiller|headache' }),
    period_days: z
        .number()
        .int()
        .optional()
        .meta({ description: 'Days to analyze (default 365)', example: 365 }),
    trigger_type: eventTriggerTypeSchema,
    trigger_value: z
        .string()
        .meta({ description: 'Trigger activity type or tag pattern', example: 'exercise' }),
})
    .meta({ id: 'EventProbabilityBody' });
/** Lag window result */
export const lagWindowResultSchema = z
    .object({
    occurrences: z.number().int(),
    probability: z.number().meta({ description: 'P(outcome | trigger)' }),
    relative_risk: z.number().meta({ description: 'Risk ratio compared to baseline' }),
})
    .meta({ id: 'LagWindowResult' });
/** Event Probability result data */
export const eventProbabilityDataSchema = z
    .object({
    baseline: z.object({
        description: z.string(),
        probability: z.number(),
    }),
    outcome: z.object({
        pattern: z.string(),
        type: z.literal('tag'),
    }),
    period: z.object({
        end: z.string(),
        start: z.string(),
    }),
    post_trigger: z.record(z.string(), lagWindowResultSchema).meta({
        description: 'Probability for each lag window',
    }),
    sample_size: z.object({
        days_analyzed: z.number().int(),
        outcome_events: z.number().int(),
        trigger_events: z.number().int(),
    }),
    statistical_significance: z.object({
        chi_squared: z.number().nullable(),
        p_value: z.number().nullable(),
    }),
    trigger: z.object({
        type: eventTriggerTypeSchema,
        value: z.string(),
    }),
})
    .meta({ id: 'EventProbabilityData' });
/** Event Probability response */
export const eventProbabilityResponseSchema = createDataResponseSchema(eventProbabilityDataSchema).meta({
    id: 'EventProbabilityResponse',
});
// ============================================================================
// Generic Correlation endpoint
// ============================================================================
/** Nutrient keys that have authoritative meal-level total columns. */
export const nutrientKeySchema = z.enum(['calories', 'protein', 'carbs', 'fat', 'fiber']).meta({
    description: 'Nutrient with a per-day meal total',
    example: 'carbs',
    id: 'NutrientKey',
});
/** Threshold that turns a numeric value into a discrete event. */
export const thresholdSpecSchema = z
    .object({
    op: z.enum(['gt', 'gte', 'lt', 'lte']).meta({ description: 'Comparison operator' }),
    value: z.number().meta({ description: 'Threshold value' }),
})
    .meta({ id: 'ThresholdSpec' });
/** Trigger condition type */
export const triggerConditionTypeSchema = z
    .enum(['activity', 'tag', 'productivity_category', 'productivity_app', 'nutrition'])
    .meta({
    description: 'Type of trigger event',
    example: 'tag',
    id: 'TriggerConditionType',
});
/** Trigger condition schema */
export const triggerConditionSchema = z
    .object({
    min_count: z.number().int().optional().meta({
        description: 'Minimum occurrences within the window (default: 1). In event-outcome mode (day-granular) this counts distinct days with the event, not raw occurrences.',
        example: 3,
    }),
    nutrient: nutrientKeySchema.optional().meta({ description: 'Nutrient to use when type is "nutrition"' }),
    pattern: z.string().optional().meta({
        description: 'Pattern to match (regex for tags, exact match for activity types). Omit for nutrition triggers.',
        example: 'meditation',
    }),
    threshold: thresholdSpecSchema
        .optional()
        .meta({ description: 'Threshold turning a daily value into a trigger (default: value > 0)' }),
    type: triggerConditionTypeSchema,
    window_days: z.number().int().optional().meta({
        description: 'Rolling window in days for counting occurrences (default: 1)',
        example: 7,
    }),
})
    .meta({ id: 'TriggerCondition' });
/** Tag outcome schema */
export const tagOutcomeSchema = z
    .object({
    pattern: z.string().meta({ description: 'Regex pattern for outcome tag', example: 'headache|migraine' }),
    type: z.literal('tag'),
})
    .meta({ id: 'TagOutcome' });
/** Metric outcome schema */
export const metricOutcomeSchema = z
    .object({
    aggregation: z
        .enum(['mean', 'min', 'max', 'last'])
        .optional()
        .meta({ description: 'Aggregation method (default: mean)' }),
    metric: z
        .string()
        .meta({ description: 'Metric name (e.g., weight, body_fat, hrv_rmssd)', example: 'weight' }),
    type: z.literal('metric'),
})
    .meta({ id: 'MetricOutcome' });
/** Productivity outcome schema */
export const productivityOutcomeSchema = z
    .object({
    app: z.string().optional().meta({ description: 'Specific app to measure time in', example: 'vscode' }),
    category: z.string().optional().meta({
        description: 'Category to measure time in',
        example: 'Software Development',
    }),
    type: z.literal('productivity'),
})
    .meta({ id: 'ProductivityOutcome' });
/**
 * Event outcome schema — treats outcome entries as discrete onsets rather than
 * averaging them. Designed for presence-only metrics (e.g. back_pain) where a
 * row only exists on "bad" days. See issue #792.
 */
export const eventOutcomeSchema = z
    .object({
    collapse_gap_days: z.number().int().optional().meta({
        description: 'Consecutive event days within this gap collapse into one onset (default: 3)',
        example: 3,
    }),
    metric: z.string().optional().meta({
        description: 'Metric name when source is "metric" (e.g. back_pain)',
        example: 'back_pain',
    }),
    pattern: z.string().optional().meta({
        description: 'Regex for the outcome tag when source is "tag"',
        example: 'migraine',
    }),
    source: z
        .enum(['metric', 'tag'])
        .meta({ description: 'Where outcome events come from', example: 'metric' }),
    threshold: thresholdSpecSchema
        .optional()
        .meta({ description: 'Value threshold defining an event (default: value > 0)' }),
    type: z.literal('event'),
})
    .meta({ id: 'EventOutcome' });
/** Outcome configuration (discriminated union) */
export const outcomeConfigSchema = z.discriminatedUnion('type', [
    tagOutcomeSchema,
    metricOutcomeSchema,
    productivityOutcomeSchema,
    eventOutcomeSchema,
]);
/** Denominator universe for event-outcome analysis. */
export const denominatorSchema = z.enum(['known', 'all']).meta({
    description: 'Which days form the denominator: "known" = days where outcome status is known (default), "all" = every day in the window',
    example: 'known',
    id: 'CorrelationDenominator',
});
/** Generic correlation request body */
export const genericCorrelationBodySchema = z
    .object({
    denominator: denominatorSchema.optional().meta({
        description: 'Denominator universe for event outcomes (default: known)',
    }),
    lag_windows: z
        .array(z.string())
        .optional()
        .meta({
        description: 'Time windows to analyze (e.g., ["12h", "24h", "7d"])',
        example: ['24h', '48h', '7d'],
    }),
    outcome: outcomeConfigSchema.meta({ description: 'Outcome to measure' }),
    period_days: z.number().int().optional().meta({
        description: 'Days to analyze (default: 90). Ignored when period_start is given.',
        example: 90,
    }),
    period_end: z.string().optional().meta({
        description: 'Inclusive end of the analysis regime (YYYY-MM-DD, default: today)',
        example: '2024-12-31',
    }),
    period_start: z.string().optional().meta({
        description: 'Inclusive start of the analysis regime (YYYY-MM-DD). Scopes to a behavioural era.',
        example: '2019-09-28',
    }),
    triggers: z.array(triggerConditionSchema).min(1).meta({
        description: 'Trigger conditions (all must be satisfied for a match)',
    }),
})
    .meta({ id: 'GenericCorrelationBody' });
/** Tag lag result */
export const tagLagResultSchema = z
    .object({
    occurrences: z.number().int(),
    probability: z.number().meta({ description: 'P(outcome | trigger)' }),
    relative_risk: z.number().meta({ description: 'Risk ratio compared to baseline' }),
})
    .meta({ id: 'GenericTagLagResult' });
/** Metric lag result */
export const metricLagResultSchema = z
    .object({
    delta_from_baseline: z.number().nullable().meta({ description: 'Difference from baseline mean' }),
    mean: z.number().nullable().meta({ description: 'Mean value in the lag window' }),
    sample_count: z.number().int(),
    stddev: z.number().nullable().meta({ description: 'Standard deviation' }),
})
    .meta({ id: 'MetricLagResult' });
/** Productivity lag result */
export const productivityLagResultSchema = z
    .object({
    avg_minutes_per_day: z.number().meta({ description: 'Average minutes per day' }),
    delta_from_baseline: z.number().nullable().meta({ description: 'Difference from baseline' }),
    total_minutes: z.number().meta({ description: 'Total minutes in the lag window' }),
})
    .meta({ id: 'ProductivityLagResult' });
/** Generic lag result (union) */
export const genericLagResultSchema = z.union([
    tagLagResultSchema,
    metricLagResultSchema,
    productivityLagResultSchema,
]);
/** Tag baseline stats */
export const tagBaselineSchema = z
    .object({
    description: z.string(),
    probability: z.number(),
})
    .meta({ id: 'TagBaseline' });
/** Metric baseline stats */
export const metricBaselineSchema = z
    .object({
    mean: z.number().nullable(),
    sample_count: z.number().int(),
    stddev: z.number().nullable(),
})
    .meta({ id: 'MetricBaseline' });
/** Productivity baseline stats */
export const productivityBaselineSchema = z
    .object({
    avg_minutes_per_day: z.number(),
    total_minutes: z.number(),
})
    .meta({ id: 'ProductivityBaseline' });
/** Generic baseline stats (union) */
export const genericBaselineSchema = z.union([
    tagBaselineSchema,
    metricBaselineSchema,
    productivityBaselineSchema,
]);
/**
 * Exposure-corrected result for a single lag window (event-outcome mode).
 * Reports the reverse conditional P(recent trigger | onset) as the headline,
 * beside the base rate, relative risk + 95% CI, and a significance test.
 */
export const lagExposureResultSchema = z
    .object({
    base_rate: z.number().meta({ description: 'P(a known day is exposed to a recent trigger)' }),
    chi_squared: z.number().nullable().meta({ description: 'Chi-squared statistic (null if Fisher used)' }),
    ci_high: z.number().nullable().meta({ description: 'Upper bound of the 95% CI for relative risk' }),
    ci_low: z.number().nullable().meta({ description: 'Lower bound of the 95% CI for relative risk' }),
    exposed_days: z.number().int().meta({ description: 'Known days within lag of a trigger' }),
    expected_onsets_exposed: z
        .number()
        .meta({ description: 'Onsets expected to be exposed under the base rate' }),
    known_days: z.number().int().meta({ description: 'Known-day denominator size' }),
    lag: z.string().meta({ description: 'Lag window label' }),
    lag_days: z.number().int().meta({ description: 'Lag window length in days' }),
    onsets: z.number().int().meta({ description: 'Onset count after collapsing' }),
    onsets_exposed: z.number().int().meta({ description: 'Onsets that were exposed to a recent trigger' }),
    p_value: z.number().meta({ description: 'Two-sided p-value' }),
    relative_risk: z.number().nullable().meta({ description: 'Onset rate exposed vs unexposed' }),
    reverse_conditional: z.number().meta({ description: 'P(recent trigger | onset) — the headline' }),
    risk_difference: z.number().meta({ description: 'Onset rate difference (exposed minus unexposed)' }),
    test: z.enum(['chi_squared', 'fisher']).meta({ description: 'Significance test used' }),
    unexposed_days: z.number().int().meta({ description: 'Known days not within lag of a trigger' }),
})
    .meta({ id: 'LagExposureResult' });
/** Event-outcome analysis block (present when outcome.type === 'event'). */
export const eventOutcomeDataSchema = z
    .object({
    collapse_gap_days: z.number().int(),
    denominator: denominatorSchema,
    known_days: z.number().int().meta({ description: 'Known-day denominator size' }),
    onsets: z.number().int().meta({ description: 'Onset count after collapsing' }),
    outcome_days: z.number().int().meta({ description: 'Raw outcome-day count before collapsing' }),
    per_lag: z.array(lagExposureResultSchema).meta({ description: 'Exposure result per lag window' }),
    trigger_days: z.number().int().meta({ description: 'Days all trigger conditions were met' }),
})
    .meta({ id: 'EventOutcomeData' });
/** Generic correlation result data */
export const genericCorrelationDataSchema = z
    .object({
    baseline: genericBaselineSchema.meta({ description: 'Baseline statistics (periods without triggers)' }),
    event_outcome: eventOutcomeDataSchema
        .optional()
        .meta({ description: 'Exposure-corrected analysis (present when outcome.type === "event")' }),
    outcome: outcomeConfigSchema.meta({ description: 'Outcome configuration' }),
    period: z.object({
        days: z.number().int(),
        end: z.string(),
        start: z.string(),
    }),
    post_trigger: z.record(z.string(), genericLagResultSchema).meta({
        description: 'Results for each lag window',
    }),
    statistical_significance: z.object({
        chi_squared: z.number().nullable(),
        p_value: z.number().nullable(),
    }),
    triggers: z.array(triggerConditionSchema).meta({ description: 'Trigger conditions used' }),
    windows_matched: z
        .number()
        .int()
        .meta({ description: 'Number of windows where all conditions were met' }),
})
    .meta({ id: 'GenericCorrelationData' });
/** Generic correlation response */
export const genericCorrelationResponseSchema = createDataResponseSchema(genericCorrelationDataSchema).meta({
    id: 'GenericCorrelationResponse',
});
// ============================================================================
// MCP input schemas (typed params, complementing Express string-based queries)
// ============================================================================
/** Activity impact MCP input schema (typed numbers instead of query strings) */
export const activityImpactInputSchema = z
    .object({
    activity: z
        .string()
        .meta({ description: 'The activity or tag name to analyze (e.g., "gym", "coffee", "meditation")' }),
    activity_type: activityImpactTypeSchema.meta({ description: 'Type of activity to search for' }),
    period_days: z
        .number()
        .int()
        .optional()
        .meta({ description: 'Number of days to analyze. Defaults to 90.' }),
    window_minutes: z
        .number()
        .int()
        .optional()
        .meta({ description: 'Minutes to analyze before/after the activity. Defaults to 30.' }),
})
    .meta({ id: 'ActivityImpactInput' });
/** Event probability MCP input schema (typed numbers instead of query strings) */
export const eventProbabilityInputSchema = z
    .object({
    lag_windows: z.array(z.string()).optional().meta({
        description: 'Time windows to analyze (e.g., ["12h", "24h", "36h", "48h"]). Uses hours (h) or days (d).',
    }),
    outcome_pattern: z
        .string()
        .meta({ description: 'Regex pattern for outcome tags (e.g., "headache|migraine", "good_sleep")' }),
    period_days: z
        .number()
        .int()
        .optional()
        .meta({ description: 'Number of days to analyze. Defaults to 365.' }),
    trigger_type: eventTriggerTypeSchema.meta({ description: 'Type of trigger event' }),
    trigger_value: z
        .string()
        .meta({ description: 'Trigger activity type or tag pattern (e.g., "exercise", "gym", "coffee")' }),
})
    .meta({ id: 'EventProbabilityInput' });
/** HRV correlation MCP input schema */
export const hrvCorrelationInputSchema = z
    .object({
    context_metric: hrvContextMetricSchema
        .optional()
        .meta({ description: 'Autonomic metric to correlate against (default: hrv_rmssd)' }),
    period_days: z
        .number()
        .int()
        .optional()
        .meta({ description: 'Number of days to analyze. Defaults to 30.' }),
})
    .meta({ id: 'HrvCorrelationInput' });
// ============================================================================
// Selectors + continuous correlation (exploratory engine)
// ============================================================================
/** A data dimension that resolves to events and a daily series. */
export const selectorSchema = z
    .discriminatedUnion('kind', [
    z.object({
        kind: z.literal('tag'),
        pattern: z.string().meta({ description: 'Regex matched against activity_type' }),
    }),
    z.object({
        kind: z.literal('activity'),
        measure: z
            .enum(['count', 'duration_min'])
            .optional()
            .meta({ description: 'Daily measure (default: count)' }),
        pattern: z.string().meta({ description: 'Regex matched against activity_type' }),
    }),
    z.object({
        agg: z.enum(['avg', 'sum']).optional().meta({ description: 'Daily aggregation (default: avg)' }),
        kind: z.literal('metric'),
        metric: z.string().meta({ description: 'Metric name', example: 'sleep_score' }),
        threshold: thresholdSpecSchema.optional(),
    }),
    z.object({
        kind: z.literal('nutrition'),
        nutrient: nutrientKeySchema,
        threshold: thresholdSpecSchema.optional(),
    }),
    z.object({
        kind: z.enum(['productivity_category', 'productivity_app']),
        pattern: z.string().meta({ description: 'Regex matched against category/app' }),
    }),
])
    .meta({ id: 'CorrelationSelector' });
/**
 * Short axis label for a selector, including its unit where known. Shared single
 * source for the correlation scatter axes on the web (Explore + article blocks)
 * and the backend server-side scatter renderer (article-block PNGs), so an axis
 * reads the same in-app and in a federated/exported image.
 */
export const describeSelectorAxis = (selector) => {
    switch (selector.kind) {
        case 'metric': {
            if (!isValidMetric(selector.metric))
                return selector.metric || 'metric';
            const unit = metricUnits[selector.metric];
            return unit ? `${selector.metric} (${unit})` : selector.metric;
        }
        case 'nutrition':
            return `${selector.nutrient} (${selector.nutrient === 'calories' ? 'kcal' : 'g'})`;
        case 'activity':
            return `${selector.pattern || 'activity'} (${selector.measure === 'duration_min' ? 'min' : 'count'})`;
        case 'productivity_category':
        case 'productivity_app':
            return `${selector.pattern || 'productivity'} (min)`;
        case 'tag':
        default:
            return `${selector.pattern || 'tag'} (count)`;
    }
};
/**
 * Ordinary least-squares fit `y = slope·x + intercept`, or null when undefined
 * (fewer than 2 points, mismatched lengths, or zero x-variance). Shared by the
 * web and backend correlation scatter renderers.
 */
export const linearRegression = (xs, ys) => {
    const n = xs.length;
    if (n < 2 || xs.length !== ys.length)
        return null;
    let sx = 0;
    let sy = 0;
    let sxx = 0;
    let sxy = 0;
    for (let i = 0; i < n; i++) {
        sx += xs[i];
        sy += ys[i];
        sxx += xs[i] * xs[i];
        sxy += xs[i] * ys[i];
    }
    const denom = n * sxx - sx * sx;
    if (denom === 0)
        return null;
    const slope = (n * sxy - sx * sy) / denom;
    return { intercept: (sy - slope * sx) / n, slope };
};
/** How partially-logged nutrition days are treated in continuous correlation. */
export const nutritionCompletenessSchema = z.enum(['all', 'complete_only']).meta({
    description: "How to treat partial nutrition days: 'all' keeps every known day; 'complete_only' drops days without real macros (flag-only). n_complete is reported either way.",
    id: 'NutritionCompleteness',
});
/** Continuous correlation request body */
export const continuousCorrelationBodySchema = z
    .object({
    lag_days: z.number().int().optional().meta({
        description: 'Days the outcome lags the trigger (default: 0)',
        example: 1,
    }),
    nutrition_completeness: nutritionCompletenessSchema
        .optional()
        .meta({ description: 'Filter for partial nutrition days (default: all)' }),
    outcome: selectorSchema.meta({ description: 'Outcome dimension' }),
    period_days: z.number().int().optional().meta({ description: 'Days to analyze (default: 90)' }),
    period_end: z.string().optional().meta({ description: 'Inclusive regime end (YYYY-MM-DD)' }),
    period_start: z.string().optional().meta({ description: 'Inclusive regime start (YYYY-MM-DD)' }),
    trigger: selectorSchema.meta({ description: 'Trigger / predictor dimension' }),
})
    .meta({ id: 'ContinuousCorrelationBody' });
/** Single aligned point in a continuous correlation. */
export const alignedPointSchema = z
    .object({
    date: z.string(),
    outcome: z.number(),
    trigger: z.number(),
})
    .meta({ id: 'AlignedPoint' });
/** Welch two-sample t-test result. */
export const welchResultSchema = z
    .object({
    df: z.number().meta({ description: 'Welch–Satterthwaite degrees of freedom' }),
    p_value: z.number().meta({ description: 'Two-sided p-value' }),
    t: z.number().meta({ description: 't statistic (positive when present-group mean is higher)' }),
})
    .meta({ id: 'WelchResult' });
/** Mann–Whitney U test result. */
export const mannWhitneyResultSchema = z
    .object({
    p_value: z.number().meta({ description: 'Two-sided p-value (normal approximation)' }),
    rank_biserial: z.number().meta({
        description: 'Rank-biserial effect size in [-1, 1] (positive when present tends to exceed absent)',
    }),
    u: z.number().meta({ description: 'U statistic for the present group' }),
})
    .meta({ id: 'MannWhitneyResult' });
/**
 * Present-vs-absent comparison of a continuous outcome for a binary/presence
 * trigger — group means, their difference, effect size and group-difference
 * tests. Answers "how much does X change Y?" where a Pearson r on a 0/1 trigger
 * would mislead.
 */
export const groupComparisonSchema = z
    .object({
    cohens_d: z.number().nullable().meta({ description: "Cohen's d (pooled SD)" }),
    difference: z.number().nullable().meta({ description: 'mean_with − mean_without' }),
    mann_whitney: mannWhitneyResultSchema.nullable(),
    mean_with: z.number().nullable().meta({ description: 'Mean outcome on days the trigger was present' }),
    mean_without: z.number().nullable().meta({ description: 'Mean outcome on days the trigger was absent' }),
    n_with: z.number().int().meta({ description: 'Days the trigger was present (value > 0)' }),
    n_without: z.number().int().meta({ description: 'Days the trigger was absent (value 0)' }),
    trigger_is_binary: z.boolean().meta({ description: 'True when every aligned trigger value is 0 or 1' }),
    welch: welchResultSchema.nullable(),
})
    .meta({ id: 'GroupComparison' });
/** Continuous correlation result data */
export const continuousCorrelationDataSchema = z
    .object({
    group_comparison: groupComparisonSchema
        .nullable()
        .meta({ description: 'Present-vs-absent group comparison; null when there is no split to compare' }),
    lag_days: z.number().int(),
    n: z.number().int().meta({ description: 'Number of aligned day pairs' }),
    n_complete: z.number().int().nullable().meta({
        description: 'Of the n aligned pairs, how many have complete nutrition on every nutrition side; null when no side is nutrition',
    }),
    outcome: selectorSchema,
    pearson: z.number().nullable().meta({ description: 'Pearson correlation (-1..1)' }),
    pearson_p: z
        .number()
        .nullable()
        .meta({ description: 'Two-sided p-value for the Pearson correlation (null when not estimable)' }),
    period: z.object({ days: z.number().int(), end: z.string(), start: z.string() }),
    series: z.array(alignedPointSchema).meta({ description: 'Aligned daily points for plotting' }),
    spearman: z.number().nullable().meta({ description: 'Spearman rank correlation (-1..1)' }),
    trigger: selectorSchema,
})
    .meta({ id: 'ContinuousCorrelationData' });
export const continuousCorrelationResponseSchema = createDataResponseSchema(continuousCorrelationDataSchema).meta({ id: 'ContinuousCorrelationResponse' });
// ============================================================================
// Selector discovery (populates the explore UI pickers)
// ============================================================================
/** One selectable option in a discovery category. */
export const selectorOptionSchema = z
    .object({
    label: z.string().meta({ description: 'Human-readable label' }),
    value: z.string().meta({ description: 'Selector value (metric name, pattern, nutrient, ...)' }),
})
    .meta({ id: 'SelectorOption' });
/** Available selectors grouped by kind. */
export const correlationSelectorsDataSchema = z
    .object({
    activity_types: z.array(selectorOptionSchema),
    metrics: z.array(selectorOptionSchema),
    nutrients: z.array(selectorOptionSchema),
    productivity_categories: z.array(selectorOptionSchema),
    tags: z.array(selectorOptionSchema),
})
    .meta({ id: 'CorrelationSelectorsData' });
export const correlationSelectorsResponseSchema = createDataResponseSchema(correlationSelectorsDataSchema).meta({ id: 'CorrelationSelectorsResponse' });
//# sourceMappingURL=correlations.js.map